import { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import orderApi from '_api/order'
import { updateDeliveryCombinedPayment } from '_redux/modules/delivery'
import {
  updateOrderCombinedPayment,
  updateOrderStatus,
} from '_redux/modules/event'
import {
  BUYER_PAID,
  BUYER_REFUNDED,
  BUYER_UNPAID,
  HOST_ORDER_CANCELED,
  HOST_ORDER_COLLECTED,
  HOST_ORDER_UNPAID,
  HOST_ORDER_VERIFIED,
  HOST_ORDER_SUBMITTED_UNPAID,
  HOST_ORDER_SUBMITTED_VERIFIED,
  HOST_ORDER_SUBMITTED_COLLECTED,
  HOST_ORDER_REFUNDED,
} from '_utils/constant'

export default function useOrderStatusActions({
  targetEventId,
  isByAdmin,
  listOrder,
  setTimeRender,
  openRefundModal,
  detailModal,
  warningModal,
  paymentModal,
}) {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const [updatingRef, setUpdatingRef] = useState(null)

  const updateOrderStatusAPI = useCallback(
    async (_ref, _data) => {
      dispatch(updateOrderStatus(targetEventId, _ref, _data.status, _data.buyerStatus))
      await orderApi.updateOrderStatus(_ref, _data)
      if (setTimeRender) setTimeRender((prevState) => prevState + 1)
    },
    [dispatch, targetEventId, setTimeRender]
  )

  const updateOrderStatusAndBuyerStatusAPI = useCallback(
    async (_ref, _data) => {
      dispatch(updateOrderStatus(targetEventId, _ref, _data.status, _data.buyerStatus))
      await orderApi.updateOrderStatusAndBuyerStatus(_ref, _data)
      if (setTimeRender) setTimeRender((prevState) => prevState + 1)
    },
    [dispatch, targetEventId, setTimeRender]
  )

  const updateOrderCombinedPaymentAPI = useCallback(
    async (_data) => {
      dispatch(
        updateOrderCombinedPayment(
          _data.combinedPaymentRef,
          _data.status,
          _data.placerStatus
        )
      )
      dispatch(
        updateDeliveryCombinedPayment(
          _data.combinedPaymentRef,
          _data.status,
          _data.placerStatus
        )
      )
      await orderApi.updateInvoicesStatus(_data)
      if (setTimeRender) setTimeRender((prevState) => prevState + 1)
    },
    [dispatch, setTimeRender]
  )

  const handleConfirmRefund = useCallback(
    async (refunds, selectedOrders) => {
      try {
        await orderApi.refundHitPayOrder({ refunds })
        selectedOrders.forEach((o) => {
          const orderEid = o.eid || targetEventId
          dispatch(updateOrderStatus(orderEid, o.ref, HOST_ORDER_REFUNDED, o.buyerStatus))
        })
        if (setTimeRender) setTimeRender((prevState) => prevState + 1)
        if (addToast) {
          addToast('Refund request processed successfully', {
            appearance: 'success',
            autoDismiss: true,
          })
        }
      } catch (error) {
        const { msgResp } = error || {}
        if (addToast) {
          addToast(msgResp || 'Failed to process refund', {
            appearance: 'error',
            autoDismiss: true,
          })
        }
        throw error
      }
    },
    [dispatch, targetEventId, setTimeRender, addToast]
  )

  const handleInvalidPayment = useCallback(
    async (ref, _status, _combinedPaymentRef) => {
      if (isByAdmin) {
        if (addToast) addToast('You are not allowed to cancel order', { appearance: 'error', autoDismiss: true })
        return
      }

      try {
        const r = window.confirm('Are you sure this is invalid payment?')
        if (r === true) {
          if (_combinedPaymentRef) {
            const _data = {
              combinedPaymentRef: _combinedPaymentRef,
              status: HOST_ORDER_UNPAID,
              placerStatus: BUYER_UNPAID,
            }
            await updateOrderCombinedPaymentAPI(_data)
          } else {
            dispatch(updateOrderStatus(targetEventId, ref, _status, BUYER_UNPAID))
            await orderApi.updateOrderBuyerStatus(ref, { buyerStatus: BUYER_UNPAID })
            if (setTimeRender) setTimeRender((prevState) => prevState + 1)
          }
          if (paymentModal) paymentModal.closeModal()
        }
      } catch (error) {
        const { msgResp } = error
        if (addToast && msgResp) addToast(msgResp, { appearance: 'error', autoDismiss: true })
        if (paymentModal) paymentModal.closeModal()
      }
    },
    [
      isByAdmin,
      targetEventId,
      addToast,
      updateOrderCombinedPaymentAPI,
      dispatch,
      setTimeRender,
      paymentModal,
    ]
  )

  const handleCancelOrder = useCallback(
    async (e, ref, _status, _buyerStatus, _combinedPaymentRef) => {
      if (e && e.preventDefault) e.preventDefault()
      if (isByAdmin) {
        if (addToast) {
          addToast('You are not allowed to cancel order', {
            appearance: 'error',
            autoDismiss: true,
          })
        }
        return
      }

      try {
        if (_status !== HOST_ORDER_CANCELED && _buyerStatus > BUYER_UNPAID) {
          if (_combinedPaymentRef) {
            const _data = {
              combinedPaymentRef: _combinedPaymentRef,
              status: HOST_ORDER_CANCELED,
              placerStatus: BUYER_REFUNDED,
            }
            await updateOrderCombinedPaymentAPI(_data)

            if (detailModal) detailModal.closeModal()
            if (warningModal) warningModal.closeModal()
          } else {
            const _data = { status: HOST_ORDER_CANCELED, buyerStatus: BUYER_REFUNDED }
            await updateOrderStatusAndBuyerStatusAPI(ref, _data)

            if (detailModal) detailModal.closeModal()
            if (warningModal) warningModal.closeModal()
          }
        } else {
          const _data = { status: HOST_ORDER_CANCELED, _buyerStatus }
          await updateOrderStatusAPI(ref, _data)

          if (detailModal) detailModal.closeModal()
          if (warningModal) warningModal.closeModal()
        }
      } catch (error) {
        const { msgResp } = error
        if (addToast && msgResp) addToast(msgResp, { appearance: 'error', autoDismiss: true })
        if (detailModal) detailModal.closeModal()
        if (warningModal) warningModal.closeModal()
      }
    },
    [
      isByAdmin,
      addToast,
      updateOrderCombinedPaymentAPI,
      updateOrderStatusAndBuyerStatusAPI,
      updateOrderStatusAPI,
      detailModal,
      warningModal,
    ]
  )

  const onChangeSelect = useCallback(
    async (e, ref, _status, _buyerStatus, _combinedPaymentRef, order) => {
      if (_status === HOST_ORDER_REFUNDED) return

      const val = Number(e.target.value)

      if (val === HOST_ORDER_REFUNDED) {
        e.target.value = _status
        const targetOrder =
          order ||
          listOrder?.find((o) => o.ref === ref) || {
            ref,
            status: _status,
            buyerStatus: _buyerStatus,
            combinedPaymentRef: _combinedPaymentRef,
          }
        if (openRefundModal) openRefundModal(targetOrder)
        return
      }

      try {
        setUpdatingRef(ref)
        if (
          (_status === HOST_ORDER_UNPAID || _status === HOST_ORDER_SUBMITTED_UNPAID) &&
          (val === HOST_ORDER_VERIFIED ||
            val === HOST_ORDER_SUBMITTED_VERIFIED ||
            val === HOST_ORDER_COLLECTED ||
            val === HOST_ORDER_SUBMITTED_COLLECTED)
        ) {
          const _data = { status: val, buyerStatus: BUYER_PAID }
          await updateOrderStatusAndBuyerStatusAPI(ref, _data)
        } else if (
          _status !== HOST_ORDER_CANCELED &&
          val === HOST_ORDER_CANCELED &&
          _buyerStatus > BUYER_UNPAID
        ) {
          if (_combinedPaymentRef) {
            const _data = {
              combinedPaymentRef: _combinedPaymentRef,
              status: HOST_ORDER_CANCELED,
              placerStatus: BUYER_REFUNDED,
            }
            await updateOrderCombinedPaymentAPI(_data)
          } else {
            const _data = { status: val, buyerStatus: BUYER_REFUNDED }
            await updateOrderStatusAndBuyerStatusAPI(ref, _data)
          }
        } else if (
          _status === HOST_ORDER_CANCELED &&
          val !== HOST_ORDER_CANCELED &&
          _buyerStatus === BUYER_REFUNDED
        ) {
          const _data = { status: val, buyerStatus: BUYER_PAID }
          await updateOrderStatusAndBuyerStatusAPI(ref, _data)
        } else {
          const _data = { status: val, buyerStatus: _buyerStatus }
          await updateOrderStatusAPI(ref, _data)
        }
      } catch (error) {
        const { msgResp } = error
        if (addToast && msgResp) addToast(msgResp, { appearance: 'error', autoDismiss: true })
        if (setTimeRender) setTimeRender((prevState) => prevState + 1)
      } finally {
        setUpdatingRef(null)
      }
    },
    [
      listOrder,
      openRefundModal,
      updateOrderStatusAndBuyerStatusAPI,
      updateOrderCombinedPaymentAPI,
      updateOrderStatusAPI,
      addToast,
      setTimeRender,
    ]
  )

  return {
    updatingRef,
    updateOrderStatusAPI,
    updateOrderStatusAndBuyerStatusAPI,
    updateOrderCombinedPaymentAPI,
    handleConfirmRefund,
    handleInvalidPayment,
    handleCancelOrder,
    onChangeSelect,
  }
}
